import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchActiveUploads, type ActiveUpload } from '../../features/upload/api';
import { leftUploads } from '../../features/upload/leftUploads';
import { UPLOAD_LIST_KEY } from '../api/freshness';
import {
  SLOW_POLL_MS,
  importProgress,
  importRoute,
  importRowOf,
  importRowOfImage,
  isSettled,
  missingDetails,
  sendingOrder,
  sessionRoute,
  targetStudy,
  uploadProgress,
  uploadWork,
  useUpload,
  type UploadSession,
} from './store';

/**
 * The top bar's list of uploads (UploadPill): one row per study, from this
 * tab's sessions (which know the bytes in flight) and the server's list of
 * the person's uploads (GET /uploads/active: other tabs, other devices, and
 * finished ones not yet opened or dismissed). A study this tab is uploading
 * into is told by its session; the server's word is taken for the rest.
 *
 * One state per row, the one that matters most to the person:
 *   failed        a transfer gave up here: Retry
 *   interrupted   a file has to be selected again (its tab is gone)
 *   details       an image waits for its anatomic site
 *   not-imported  finished, and some images did not make it
 *   sending       this tab is sending its bytes
 *   queued        this tab will send them after the study before it
 *   elsewhere     another tab or device is sending them
 *   importing     the server is converting and indexing
 *   imported      every image is published
 * Rows are ordered by that list, so the pill can name the first. On screen
 * the three that send say Uploading and the server's work says Importing,
 * nothing else (store.ts ImportState).
 */
export type RowState = 'failed' | 'interrupted' | 'details' | 'not-imported' | 'sending' | 'queued' | 'elsewhere' | 'importing' | 'imported';
const ORDER: readonly RowState[] = ['failed', 'interrupted', 'details', 'not-imported', 'sending', 'queued', 'elsewhere', 'importing', 'imported'];

export interface UploadRow {
  studyId: string;
  name: string;
  /** Collection › group, with the workspace in front when it is not the one open. */
  where: string;
  state: RowState;
  /** Images in the upload, counted as slides. */
  slides: number;
  published: number;
  notImported: number;
  /** Transfers that failed here (state `failed`), images waiting for details (`details`). */
  count: number;
  /** Refused for want of storage (store.ts storageFull, or the server's word on another tab). */
  outOfStorage: boolean;
  /** And the storage is the person's own organization's: the row offers their Usage page. */
  storageYours: boolean;
  /** The bar, 0 to 100; null when there is none to show. */
  pct: number | null;
  /** Bytes sent and to send, while this tab sends them. */
  bytes: { loaded: number; total: number } | null;
  /** This tab's session for it, when there is one. */
  sessionId: string | null;
  /** Where the row leads: the files page when it needs the person, else its progress or the study. */
  to: string;
  /** Nothing more will happen by itself: the person opens or dismisses it. */
  settled: boolean;
}

/** Whether an image is in a state the person has to act on (what an upload left on purpose is not asked about again). */
const asksThePerson = (i: ActiveUpload['images'][number]) => i.stage === 'waiting-file' || i.stage === 'needs-details' || (i.stage === 'uploading' && !i.live);

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function whereOf(u: ActiveUpload | undefined, fallback: string, workspace: string | null): string {
  if (!u) return fallback;
  const place = [u.collection_name, u.group_name].filter(Boolean).join(' › ') || fallback;
  return u.workspace && workspace && u.workspace !== workspace && u.workspace_name ? `${u.workspace_name} · ${place}` : place;
}

function studyRoute(base: string, studyId: string) {
  return `${base}/studies/${encodeURIComponent(studyId)}`;
}

/** The row for a study this tab is uploading into. */
export function rowOfSession(s: UploadSession, order: readonly string[], u: ActiveUpload | undefined, base: string, workspace: string | null): UploadRow | null {
  const studyId = targetStudy(s);
  if (!studyId || !s.files.length) return null;
  const p = uploadProgress(s.files);
  const work = uploadWork(s);
  // Refused for want of storage: after a reload they wait for their file like
  // any cut-off transfer, but what they need is room, and that is what is said.
  const refused = s.files.filter((f) => f.storageFull && (f.phase === 'failed' || f.phase === 'waiting-file'));
  const published = s.files.filter((f) => f.stage === 'published').length;
  const notImported = s.files.filter((f) => f.phase === 'done' && f.stage === 'failed').length;
  // What keeps it at 'details': a site still to give, or a detail the server refused.
  const missing = s.files.filter((f) => missingDetails(f) || f.detailsError).length;
  const state: RowState =
    p.failed > 0 || refused.length ? 'failed'
      : work === 'waiting' ? 'interrupted'
        : work === 'transferring' ? (order[0] === s.id ? 'sending' : 'queued')
          : work === 'details' ? 'details'
            : work === 'importing' ? 'importing'
              : notImported ? 'not-imported' : 'imported';
  let loaded = 0;
  let total = 0;
  for (const f of s.files) {
    total += f.size;
    loaded += f.phase === 'done' ? f.size : Math.min(f.size, Math.max(0, f.loaded));
  }
  const needsYou = state === 'failed' || state === 'interrupted' || state === 'details';
  return {
    studyId,
    name: u?.label || u?.description || s.label || 'Untitled study',
    where: whereOf(u, s.locationName, workspace),
    state,
    slides: s.files.length,
    published,
    notImported,
    count: state === 'failed' ? new Set([...s.files.filter((f) => f.phase === 'failed'), ...refused]).size : state === 'details' ? missing : 0,
    outOfStorage: refused.length > 0,
    storageYours: refused.some((f) => f.storageFull === 'yours'),
    // What is still importing: a slide already imported would hold the bar at a half it did not earn.
    pct: state === 'sending' || state === 'queued' ? p.pct : state === 'importing' ? importProgress(s.files.filter((f) => !isSettled(f)).map(importRowOf)) : null,
    bytes: state === 'sending' ? { loaded, total } : null,
    sessionId: s.id,
    to: needsYou ? sessionRoute(base, s) : state === 'imported' ? studyRoute(base, studyId) : importRoute(base, s.colId, studyId),
    settled: state === 'imported' || state === 'not-imported',
  };
}

/** The row for an upload known only from the server. Null when nothing in it is left to tell. */
export function rowOfServer(u: ActiveUpload, left: ReadonlySet<string>, base: string, workspace: string | null): UploadRow | null {
  // An upload left on purpose (Cancel) is not asked about again; what it
  // already sent still converts, and is told like any other.
  const images = u.images.filter((i) => !(left.has(i.image_id) && asksThePerson(i)));
  const slides = images.length + u.not_imported;
  if (!slides) return null;
  const count = (fn: (i: ActiveUpload['images'][number]) => boolean) => images.filter(fn).length;
  const refused = count((i) => i.refused === 'storage');
  const waiting = count((i) => i.refused !== 'storage' && (i.stage === 'waiting-file' || (i.stage === 'uploading' && !i.live)));
  const details = count((i) => i.stage === 'needs-details');
  const live = count((i) => i.stage === 'uploading' && i.live);
  const importing = count((i) => i.stage === 'converting' || i.stage === 'ingesting');
  const published = count((i) => i.stage === 'published');
  const notImported = count((i) => i.stage === 'failed') + u.not_imported;
  const state: RowState =
    refused ? 'failed' : waiting ? 'interrupted' : details ? 'details' : live ? 'elsewhere' : importing ? 'importing' : notImported ? 'not-imported' : 'imported';
  const q = new URLSearchParams();
  if (u.group) q.set('group', u.group);
  q.set('study', u.study);
  const files = u.collection ? `${base}/collections/${u.collection}/upload/files?${q}` : studyRoute(base, u.study);
  const progress = u.collection && images.length ? importRoute(base, u.collection, u.study) : studyRoute(base, u.study);
  return {
    studyId: u.study,
    name: u.label || u.description || 'Untitled study',
    where: whereOf(u, '', workspace),
    state,
    slides,
    published,
    notImported,
    count: state === 'failed' ? refused : state === 'details' ? details : 0,
    outOfStorage: refused > 0,
    storageYours: refused > 0 && u.yours,
    pct: state === 'importing'
      ? importProgress(images.filter((i) => i.stage !== 'published' && i.stage !== 'failed').map((i) => importRowOfImage({ ...i, percent: i.percent ?? undefined })))
      : null,
    bytes: null,
    sessionId: null,
    to: state === 'failed' || state === 'interrupted' || state === 'details' ? files : state === 'imported' ? studyRoute(base, u.study) : progress,
    settled: state === 'imported' || state === 'not-imported',
  };
}

/**
 * Every row, the ones that need the person first. `server` is the server's
 * list once it has answered (null before): a study this tab finished that is
 * no longer on it was dismissed elsewhere, and stays dismissed here.
 */
export function uploadRows(
  sessions: readonly UploadSession[],
  server: readonly ActiveUpload[] | null,
  opts: { base: string; workspace: string | null; left: ReadonlySet<string> },
): UploadRow[] {
  const byStudy = new Map((server ?? []).map((u) => [u.study, u]));
  const order = sendingOrder(sessions);
  const rows: UploadRow[] = [];
  const told = new Set<string>();
  for (const s of sessions) {
    const r = rowOfSession(s, order, byStudy.get(targetStudy(s) ?? ''), opts.base, opts.workspace);
    if (!r) continue;
    if (r.settled && server && !byStudy.has(r.studyId)) continue;
    told.add(r.studyId);
    rows.push(r);
  }
  for (const u of server ?? []) {
    if (told.has(u.study)) continue;
    const r = rowOfServer(u, opts.left, opts.base, opts.workspace);
    if (r) rows.push(r);
  }
  return rows.map((r, i) => ({ r, i })).sort((a, b) => ORDER.indexOf(a.r.state) - ORDER.indexOf(b.r.state) || a.i - b.i).map((x) => x.r);
}

/**
 * Whether anything on the server's list is moving by itself: bytes arriving
 * from another tab, a conversion, an index. The list is asked again only
 * while so; what waits for the person changes when they act (here, which
 * asks again at once, or elsewhere, which the next focus picks up).
 */
export const listMoving = (list: readonly ActiveUpload[] | undefined) =>
  !!list?.some((u) => u.images.some((i) => i.stage === 'uploading' || i.stage === 'converting' || i.stage === 'ingesting'));

/** The server's list, every SLOW_POLL_MS while something on it moves; not from a hidden tab, and again on focus. */
export function useUploadList(enabled: boolean) {
  return useQuery({
    queryKey: UPLOAD_LIST_KEY,
    queryFn: ({ signal }) => fetchActiveUploads(signal),
    enabled,
    refetchInterval: (q) => (listMoving(q.state.data) ? SLOW_POLL_MS : false),
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useUploadRows(opts: { enabled: boolean; base: string; workspace: string | null }): UploadRow[] {
  const sessions = useUpload((st) => st.sessions);
  const list = useUploadList(opts.enabled);
  const server = list.data ?? null;
  // What the person left on purpose filters the server's rows alone: read
  // (from storage) when those change, not on every progress patch. A Cancel
  // marks its images before it asks for the list again, so this follows it.
  // Keyed on the fetch, not the data: an identical answer keeps its object.
  const fetchedAt = list.dataUpdatedAt;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const left = useMemo(() => leftUploads(), [fetchedAt]);
  return useMemo(
    () => uploadRows(sessions, server, { base: opts.base, workspace: opts.workspace, left }),
    [sessions, server, left, opts.base, opts.workspace],
  );
}

/**
 * Bytes per second over the last few seconds of a transfer, and the seconds
 * left at that rate; null until there are three seconds to go on.
 */
export function useTransferRate(key: string | null, bytes: { loaded: number; total: number } | null): { bps: number; left: number } | null {
  const samples = useRef<{ key: string | null; points: Array<{ t: number; loaded: number }> }>({ key: null, points: [] });
  if (!key || !bytes) {
    samples.current = { key: null, points: [] };
    return null;
  }
  const now = Date.now();
  if (samples.current.key !== key) samples.current = { key, points: [] };
  const points = samples.current.points;
  const last = points[points.length - 1];
  if (!last || last.loaded !== bytes.loaded) points.push({ t: now, loaded: bytes.loaded });
  while (points.length > 2 && now - points[0].t > 10_000) points.shift();
  const first = points[0];
  const span = (now - first.t) / 1000;
  if (span < 3) return null;
  const bps = (bytes.loaded - first.loaded) / span;
  if (bps <= 0) return null;
  return { bps, left: Math.max(0, (bytes.total - bytes.loaded) / bps) };
}

export function fmtRate(bps: number): string {
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(bps >= 1e7 ? 0 : 1)} MB/s`;
  return `${Math.max(1, Math.round(bps / 1e3))} KB/s`;
}

export function fmtLeft(sec: number): string {
  if (sec < 60) return 'under a minute left';
  if (sec < 3600) return `about ${plural(Math.round(sec / 60), 'min')} left`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return `about ${h} h${m ? ` ${m} min` : ''} left`;
}

/** What the row says on its right, beside the study's name. */
export function rowText(r: UploadRow): string {
  switch (r.state) {
    case 'failed':
      return r.outOfStorage ? 'Out of storage' : `${plural(r.count, 'upload')} failed · Retry`;
    case 'interrupted':
      return 'Upload interrupted · Resume';
    case 'details':
      return `${plural(r.count, 'image')} need${r.count === 1 ? 's' : ''} details`;
    case 'not-imported':
      return r.published ? `${r.published} imported · ${r.notImported} not imported` : `${plural(r.notImported, 'image')} not imported`;
    // Queued behind another study is still its upload: the bar says how far it got.
    case 'sending':
    case 'queued':
      return `Uploading · ${r.pct ?? 0}%`;
    case 'elsewhere':
      return 'Uploading in another tab';
    case 'importing':
      return `Importing · ${r.pct ?? 0}%${r.published ? ` · ${r.published} imported` : ''}`;
    case 'imported':
      return 'Imported';
  }
}

/** What the pill says for its first row. */
export function pillText(r: UploadRow): string {
  switch (r.state) {
    case 'failed':
      return r.outOfStorage ? 'Out of storage' : `${plural(r.count, 'upload')} failed`;
    case 'interrupted':
      return 'Upload interrupted · Resume';
    case 'details':
      return `${plural(r.count, 'image')} need${r.count === 1 ? 's' : ''} details`;
    case 'not-imported':
      return `${plural(r.notImported, 'image')} not imported`;
    case 'sending':
    case 'queued':
      return `Uploading ${plural(r.slides, 'image')} · ${r.pct ?? 0}%`;
    case 'elsewhere':
      return 'Uploading in another tab';
    // The images still importing: those already in are the row's to tell ("· 1 imported").
    case 'importing':
      return `Importing ${plural(Math.max(1, r.slides - r.published - r.notImported), 'image')} · ${r.pct ?? 0}%`;
    case 'imported':
      return `${r.name} imported`;
  }
}
